import {
  ageText,
  birthDateText,
  type BottleUnit,
  formatAmount,
  fromOz,
  isPreterm,
  localCalendar,
  type TrackableType,
  type Tracked,
} from "@babia/checks";
import { useId } from "react";
import { NOTE_KIND_TITLE, weightDetail, type WeightShown } from "../../lib/entry-text.ts";
import type { PediatricReport, ReportPeriod } from "../../lib/report.ts";
import {
  addLocalDays,
  clock,
  dateline,
  minutesText,
  monthDay,
  pluralize,
  shortDuration,
  weekdayDay,
} from "../../lib/time.ts";
import { cn } from "../../lib/utils.ts";
import { Section } from "../overview/section.tsx";
import { SleepNights } from "./sleep-nights.tsx";

const MIN = 60_000;
/** One decimal, none when whole: 23.6, 8. */
const num = (x: number) => String(Math.round(x * 10) / 10);
const mins = (ms: number) => minutesText(Math.round(ms / MIN));
/** A typical wait, to the nearest 5 minutes. */
const roughly = (ms: number) => minutesText(Math.max(5, Math.round(ms / (5 * MIN)) * 5));
const when = (d: Date) => `${weekdayDay(d)}, ${clock(d)}`;

/** "Sep 23 – Sep 29, 2026 · 7 full days, then today so far" */
function periodText(p: ReportPeriod, now: Date): string {
  if (!p.days) return `Today so far, ${monthDay(now)}, ${now.getFullYear()}`;
  const last = addLocalDays(p.to, -1);
  const span = p.days === 1 ? monthDay(last) : `${monthDay(p.from)} – ${monthDay(last)}`;
  return `${span}, ${last.getFullYear()} · ${pluralize(p.days, "full day", "full days")}, then today so far`;
}

/**
 * One baby's page of the pediatrician report: the period, the daily averages
 * a checkup asks about, then the days, nights, doses, and notes behind them.
 * Bottles are in the household's unit. Diapers and sleep appear only if the
 * family tracks them. Set for paper (each baby after the first starts a new
 * sheet), and it reads the same on a phone handed across the desk.
 */
export function BabyReport({
  name,
  birthDate,
  dueDate,
  weightOf,
  report,
  period,
  now,
  tracking,
  unit,
  className,
}: {
  name: string;
  /** YYYY-MM-DD, when the parents set it: the age goes under the name. */
  birthDate: string | null;
  /** YYYY-MM-DD: for a baby born early, the corrected age goes beside the age. */
  dueDate: string | null;
  /** How a weight's number reads: the household's unit and its percentile. */
  weightOf: (note: { at: Date; weightGrams: number }) => WeightShown;
  report: PediatricReport;
  period: ReportPeriod;
  now: Date;
  tracking: Tracked;
  unit: BottleUnit;
  className?: string;
}) {
  const headingId = useId();
  return (
    <article className={className} aria-labelledby={headingId}>
      <header>
        <div className="kicker flex flex-wrap items-baseline gap-x-3 leading-tight">
          <span>Babia · Summary for the pediatrician</span>
          <span className="ml-auto">
            Prepared {dateline(now)}, {clock(now)}
          </span>
        </div>
        <h2 id={headingId} className="heading mt-2 text-[34px]">
          {name}
        </h2>
        {birthDate ? (
          <p className="mt-1 text-[15px]">
            Born {birthDateText(birthDate)} · {ageText(birthDate, localCalendar.isoDate(now))}
            {dueDate && isPreterm(birthDate, dueDate) ? (
              <>
                {" "}
                · Due {birthDateText(dueDate)}
                {localCalendar.isoDate(now) >= dueDate
                  ? ` · ${ageText(dueDate, localCalendar.isoDate(now))}, corrected`
                  : null}
              </>
            ) : null}
          </p>
        ) : null}
        <p className="mt-1 text-[15px]">{periodText(period, now)}</p>
        {period.loggingBegan ? (
          <p className="text-[13px] opacity-70">
            Logging began {weekdayDay(period.loggingBegan)} at {clock(period.loggingBegan)}, so that
            day isn't in the averages.
          </p>
        ) : null}
        <div className="head-rule" />
      </header>

      <Section
        level={3}
        className="mt-6"
        title="At a glance"
        note={
          report.countedDays
            ? `Daily averages over ${pluralize(report.countedDays, "full day", "full days")}`
            : undefined
        }
      >
        <Glance report={report} tracking={tracking} unit={unit} />
      </Section>

      <Section
        level={3}
        className="mt-8"
        title={tracking.diaper ? "Bottles and diapers" : "Bottles"}
        note="By day"
      >
        <DayTable report={report} diaper={tracking.diaper} unit={unit} />
      </Section>

      {tracking.sleep ? (
        <Section
          level={3}
          className="mt-8 break-inside-avoid"
          title="Sleep"
          note="By night, noon to noon · solid is the longest stretch · night is 7 pm – 7 am (shaded)"
        >
          <SleepNights nights={report.nights} />
          {report.leftOut.length ? (
            <p className="mt-2 text-[12px] opacity-70">
              Left out:{" "}
              {report.leftOut
                .map(
                  (e) =>
                    `a ${mins((e.endedAt ?? now).getTime() - e.at.getTime())} sleep from ${when(e.at)}`,
                )
                .join("; ")}{" "}
              — too long to be real, probably a missed Wake up tap.
            </p>
          ) : null}
        </Section>
      ) : null}

      <Section level={3} className="mt-8" title="Medicine" note="Every dose logged, through now">
        {report.meds.length ? (
          <ul className="mt-2">
            {report.meds.map((m) => (
              <li
                key={m.label}
                className="break-inside-avoid border-t border-rule py-2 first:border-t-0"
              >
                <div className="heading text-[16px]">
                  {m.label}{" "}
                  <span className="text-[13px] font-normal opacity-70">
                    · {pluralize(m.doses.length, "dose", "doses")} on{" "}
                    {pluralize(m.days, "day", "days")}
                    {m.each ? ` · ${m.each} each` : null}
                  </span>
                </div>
                <p className="mt-0.5 text-[13px] tabular-nums">
                  {m.doses
                    .map((d) => (d.dose && !m.each ? `${when(d.at)} (${d.dose})` : when(d.at)))
                    .join(" · ")}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <None />
        )}
      </Section>

      <Section level={3} className="mt-8" title="Notes" note="Every note, through now">
        {report.notes.length ? (
          <ul className="mt-2 text-[13px]">
            {report.notes.map((n) => (
              <li
                key={n.id}
                className="grid break-inside-avoid grid-cols-[auto_minmax(0,1fr)] gap-x-4 border-t border-rule py-1.5 first:border-t-0"
              >
                <span className="whitespace-nowrap tabular-nums opacity-70">{when(n.at)}</span>
                <span className="whitespace-pre-line break-words">
                  {n.kind === "general" ? null : (
                    <span className="heading">
                      {NOTE_KIND_TITLE[n.kind]}
                      {n.text || n.weightGrams !== null ? " — " : null}
                    </span>
                  )}
                  {n.weightGrams === null ? null : (
                    <span className="tabular-nums">
                      {(() => {
                        const shown = weightOf({ at: n.at, weightGrams: n.weightGrams });
                        return weightDetail(n.weightGrams, shown.unit, shown.percentile);
                      })()}
                      {n.text ? " — " : null}
                    </span>
                  )}
                  {n.text}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <None />
        )}
      </Section>

      <footer className="mt-8 border-t border-ink pt-2 text-[11px] leading-snug opacity-70">
        From the family's Babia log. The numbers are what was logged: a missed tap reads as a lower
        count or a longer gap. Averages cover full days and whole nights; lighter rows (today so
        far, a night still going, the day logging began) are left out, and a dash means none of that
        kind was logged that day, so it isn't averaged as a zero.
      </footer>
    </article>
  );
}

function None() {
  return <p className="py-3 text-[14px] italic opacity-60">None logged.</p>;
}

/** The headline numbers, one row per kind of entry the family tracks. */
function Glance({
  report,
  tracking,
  unit,
}: {
  report: PediatricReport;
  tracking: Tracked;
  unit: BottleUnit;
}) {
  const { feeding, diapers, sleep, meds } = report;
  if (!report.countedDays)
    return (
      <p className="py-3 text-[14px] italic opacity-60">
        No full day logged yet. Averages start after the first whole day.
      </p>
    );
  // Each kind is averaged over the days it was logged; say so when that differs.
  const over = (n: number, one: string, many: string) =>
    n !== report.countedDays ? ` (over ${pluralize(n, one, many)})` : "";
  const rows: {
    label: string;
    main: string;
    sub: (string | null)[];
    empty?: boolean;
    /** Shown only when the family tracks it. */
    type?: TrackableType;
  }[] = [
    feeding
      ? {
          label: "Bottles",
          main: `${formatAmount(feeding.oz.avg, unit)} a day${over(feeding.days, "day", "days")}`,
          sub: [
            [
              fromOz(feeding.oz.min, unit) !== fromOz(feeding.oz.max, unit)
                ? `${fromOz(feeding.oz.min, unit)}–${formatAmount(feeding.oz.max, unit)}`
                : null,
              `${num(feeding.bottlesPerDay)} bottles of ${formatAmount(feeding.ozPerBottle, unit)}`,
              feeding.typicalGapMs !== null
                ? `usually ${roughly(feeding.typicalGapMs)} apart`
                : null,
            ]
              .filter(Boolean)
              .join(" · "),
            feeding.longestGapMs !== null
              ? `Longest gap ${mins(feeding.longestGapMs)} on average`
              : null,
            feeding.formulas.length
              ? `Formula: ${feeding.formulas.map((f) => `${f.label} (${f.bottles})`).join(", ")}`
              : null,
          ],
        }
      : { label: "Bottles", main: "Not logged", sub: [], empty: true },
    diapers
      ? {
          label: "Diapers",
          main: `${num(diapers.wet)} wet · ${num(diapers.dirty)} dirty a day${over(diapers.days, "day", "days")}`,
          sub: [],
          type: "diaper",
        }
      : { label: "Diapers", main: "Not logged", sub: [], empty: true, type: "diaper" },
    sleep
      ? {
          label: "Sleep",
          type: "sleep",
          main: `${mins(sleep.totalMs)} a day${over(sleep.nights, "night", "nights")}`,
          sub: [
            `${mins(sleep.nightMs)} at night (7 pm – 7 am), ${mins(sleep.dayMs)} by day`,
            sleep.longest
              ? `Longest stretch ${mins(sleep.longest.avgMs)} on average; ${mins(sleep.longest.best.ms)} at most, from ${when(sleep.longest.best.at)}`
              : null,
          ],
        }
      : {
          label: "Sleep",
          type: "sleep",
          main: report.nights.some((n) => n.segments.length)
            ? "No whole night logged yet"
            : "Not logged",
          sub: [],
          empty: true,
        },
    {
      label: "Medicine",
      main: meds.length
        ? meds.map((m) => `${m.label} (${pluralize(m.doses.length, "dose", "doses")})`).join(", ")
        : "None logged",
      sub: [],
      empty: !meds.length,
    },
  ];
  return (
    <dl className="mt-1">
      {rows
        .filter((r) => !r.type || tracking[r.type])
        .map((r) => (
          <div
            key={r.label}
            className="grid break-inside-avoid grid-cols-[84px_minmax(0,1fr)] gap-x-3 border-t border-rule py-2 first:border-t-0"
          >
            <dt className="kicker pt-1.5">{r.label}</dt>
            <dd>
              <div
                className={cn("heading text-[18px]", r.empty && "font-normal italic opacity-60")}
              >
                {r.main}
              </div>
              {r.sub.filter(Boolean).map((s) => (
                <p key={s} className="text-[13px] leading-snug opacity-80">
                  {s}
                </p>
              ))}
            </dd>
          </div>
        ))}
    </dl>
  );
}

/** None of that kind logged that day: not a zero. */
const DASH = "—";
const TH = "py-1.5 pr-2 text-right font-normal opacity-60 print:py-1";
const TD = "py-1.5 pr-2 text-right print:py-1";

/** Every day's bottles (and diapers, if tracked), oldest first, with the averages beneath. */
function DayTable({
  report,
  diaper,
  unit,
}: {
  report: PediatricReport;
  diaper: boolean;
  unit: BottleUnit;
}) {
  const { feeding, diapers } = report;
  return (
    <table className="mt-2 w-full border-collapse text-left text-[13px] tabular-nums">
      <thead>
        <tr className="border-b border-ink text-[11px] uppercase tracking-[0.06em]">
          <th className="py-1.5 pr-2 font-normal opacity-60 print:py-1">Day</th>
          <th className={TH}>{unit}</th>
          <th className={TH}>Bottles</th>
          <th className={cn(TH, !diaper && "pr-0")}>Longest gap</th>
          {diaper ? (
            <>
              <th className={TH}>Wet</th>
              <th className={cn(TH, "pr-0")}>Dirty</th>
            </>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {report.days.map((d) => (
          <tr
            key={d.day.getTime()}
            className={cn("break-inside-avoid border-t border-rule", !d.counted && "opacity-60")}
          >
            <th scope="row" className="py-1.5 pr-2 font-normal whitespace-nowrap print:py-1">
              {d.isToday ? "Today so far" : weekdayDay(d.day)}
            </th>
            {d.logged ? (
              <>
                <td className={TD}>{d.bottles ? fromOz(d.oz, unit) : DASH}</td>
                <td className={TD}>{d.bottles || DASH}</td>
                <td className={cn(TD, !diaper && "pr-0")}>
                  {d.longestGapMs !== null ? shortDuration(d.longestGapMs) : DASH}
                </td>
                {diaper ? (
                  <>
                    <td className={TD}>{d.changes ? d.wet : DASH}</td>
                    <td className={cn(TD, "pr-0")}>{d.changes ? d.dirty : DASH}</td>
                  </>
                ) : null}
              </>
            ) : (
              <td colSpan={diaper ? 5 : 3} className="py-1.5 text-right italic print:py-1">
                Nothing logged
              </td>
            )}
          </tr>
        ))}
      </tbody>
      {feeding || (diaper && diapers) ? (
        <tfoot>
          <tr className="heading border-t border-ink">
            <th scope="row" className="py-1.5 pr-2 text-left font-semibold print:py-1">
              Average
            </th>
            <td className={TD}>{feeding ? fromOz(feeding.oz.avg, unit) : DASH}</td>
            <td className={TD}>{feeding ? num(feeding.bottlesPerDay) : DASH}</td>
            <td className={cn(TD, !diaper && "pr-0")}>
              {feeding?.longestGapMs != null ? shortDuration(feeding.longestGapMs) : DASH}
            </td>
            {diaper ? (
              <>
                <td className={TD}>{diapers ? num(diapers.wet) : DASH}</td>
                <td className={cn(TD, "pr-0")}>{diapers ? num(diapers.dirty) : DASH}</td>
              </>
            ) : null}
          </tr>
        </tfoot>
      ) : null}
    </table>
  );
}
